import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  avatar_url: string;
  travel_style: string;
  preferred_budget: string;
  dietary_requirements: string;
  special_notes: string;
  created_at: string;
  updated_at: string;
}

export interface SavedDestination {
  id: string;
  user_id: string;
  destination_id: number;
  destination_name: string;
  destination_location: string;
  destination_image: string;
  destination_price: string;
  created_at: string;
}

export interface JourneyRequest {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  destination: string;
  experience: string;
  check_in: string;
  check_out: string;
  guests: string;
  budget: string;
  special_requests: string;
  status: string;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  savedDestinations: SavedDestination[];
  journeyRequests: JourneyRequest[];
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: string | null }>;
  saveDestination: (dest: { id: number; name: string; location: string; image: string; price: string }) => Promise<void>;
  unsaveDestination: (destinationId: number) => Promise<void>;
  isDestinationSaved: (destinationId: number) => boolean;
  submitJourneyRequest: (data: Omit<JourneyRequest, 'id' | 'user_id' | 'status' | 'created_at'>) => Promise<{ error: string | null }>;
  refreshData: () => Promise<void>;
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedDestinations, setSavedDestinations] = useState<SavedDestination[]>([]);
  const [journeyRequests, setJourneyRequests] = useState<JourneyRequest[]>([]);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const fetchProfile = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) setProfile(data);
  }, []);

  const fetchSavedDestinations = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('saved_destinations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (data) setSavedDestinations(data);
  }, []);

  const fetchJourneyRequests = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('journey_requests')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (data) setJourneyRequests(data);
  }, []);

  const refreshData = useCallback(async () => {
    if (user) {
      await Promise.all([
        fetchProfile(user.id),
        fetchSavedDestinations(user.id),
        fetchJourneyRequests(user.id),
      ]);
    }
  }, [user, fetchProfile, fetchSavedDestinations, fetchJourneyRequests]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id);
        fetchSavedDestinations(s.user.id);
        fetchJourneyRequests(s.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id);
        fetchSavedDestinations(s.user.id);
        fetchJourneyRequests(s.user.id);
      } else {
        setProfile(null);
        setSavedDestinations([]);
        setJourneyRequests([]);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, fetchSavedDestinations, fetchJourneyRequests]);

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) return { error: error.message };
    
    if (data.user) {
      // Update profile with name
      await supabase.from('profiles').update({
        first_name: firstName,
        last_name: lastName,
      }).eq('id', data.user.id);
    }
    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setSavedDestinations([]);
    setJourneyRequests([]);
  };

  const resetPassword = async (email: string) => {
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) return { error: error.message };
    return { error: null };
  };


  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: 'Not authenticated' };
    const { error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id);
    if (error) return { error: error.message };
    await fetchProfile(user.id);
    return { error: null };
  };

  const saveDestination = async (dest: { id: number; name: string; location: string; image: string; price: string }) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    await supabase.from('saved_destinations').insert({
      user_id: user.id,
      destination_id: dest.id,
      destination_name: dest.name,
      destination_location: dest.location,
      destination_image: dest.image,
      destination_price: dest.price,
    });
    await fetchSavedDestinations(user.id);
  };

  const unsaveDestination = async (destinationId: number) => {
    if (!user) return;
    await supabase
      .from('saved_destinations')
      .delete()
      .eq('user_id', user.id)
      .eq('destination_id', destinationId);
    await fetchSavedDestinations(user.id);
  };

  const isDestinationSaved = (destinationId: number) => {
    return savedDestinations.some((sd) => sd.destination_id === destinationId);
  };

  const submitJourneyRequest = async (data: Omit<JourneyRequest, 'id' | 'user_id' | 'status' | 'created_at'>) => {
    const insertData: any = { ...data };
    // Convert empty date strings to null to satisfy date columns
    if (!insertData.check_in) insertData.check_in = null;
    if (!insertData.check_out) insertData.check_out = null;
    if (user) insertData.user_id = user.id;

    const { error } = await supabase.from('journey_requests').insert(insertData);
    if (error) {
      console.error('Journey request submission error:', error);
      return { error: error.message };
    }
    if (user) await fetchJourneyRequests(user.id);
    return { error: null };
  };


  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        savedDestinations,
        journeyRequests,
        signUp,
        signIn,
        signOut,
        resetPassword,
        updateProfile,

        saveDestination,
        unsaveDestination,
        isDestinationSaved,
        submitJourneyRequest,
        refreshData,
        showAuthModal,
        setShowAuthModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
